import _ from 'lodash'

/**
 * Paginated lists helper provider for service {@link TmrPaging}
 *
 * @alias TmrPagingProvider
 * @type {ngProvider}
 */
export class TmrPaging {
  static ngType = 'provider'

  constructor(tmrDefaultPagingParams) {
    'ngInject'
    Object.assign(this, { tmrDefaultPagingParams })
  }

  processRoute(route) {
    this.addRouteParams(route)
    this.addRouteResolves(route)
  }

  /**
   * Adds pagination parameters to a route.
   *
   * @param {TmrRoute} route
   */
  addRouteParams(route) {
    route.params = route.params ?? {}

    // Number and boolean parameters need a custom param type.
    // @see https://ui-router.github.io/ng1/docs/latest/interfaces/params.paramdeclaration.html
    let pagingParams = {
      offset: {
        value: this.tmrDefaultPagingParams.offset,
        type: 'int',
        squash: true,
      },
      limit: {
        value: this.tmrDefaultPagingParams.limit,
        type: 'int',
        squash: true,
      },
      orderBy: {
        value: this.tmrDefaultPagingParams.orderBy,
        type: 'string',
        squash: true,
      },
      asc: {
        value: this.tmrDefaultPagingParams.asc,
        type: 'bool',
        squash: true,
      },
    }

    if (route.paging === true) _.defaults(route.params, pagingParams)
    else
      route.paging.forEach(pagerId =>
        _.defaults(route.params, {
          [`limit.${pagerId}`]: pagingParams.limit,
          [`offset.${pagerId}`]: pagingParams.offset,
          [`orderBy.${pagerId}`]: pagingParams.orderBy,
          [`asc.${pagerId}`]: pagingParams.asc,
        })
      )
  }

  /**
   * Adds pagination resolves to a route.
   *
   * @param {TmrRoute} route
   */
  addRouteResolves(route) {
    route.resolve = route.resolve ?? {}
    if (route.paging === true) {
      Object.assign(route.resolve, {
        pageSize: /*@ngInject*/ $transition$ => $transition$.params().limit,
        currentPage: /*@ngInject*/ ($transition$, pageSize) =>
          $transition$.params().offset / pageSize + 1,
      })
    } else {
      Object.assign(route.resolve, {
        pageSize: /*@ngInject*/ $transition$ =>
          _.zipObject(
            route.paging,
            route.paging.map(key => $transition$.params()[`limit.${key}`])
          ),
        currentPage: /*@ngInject*/ ($transition$, pageSize) =>
          _.zipObject(
            route.paging,
            route.paging.map(
              key => $transition$.params()[`offset.${key}`] / pageSize[key] + 1
            )
          ),
      })
    }
  }

  $get($state, $stateParams) {
    'ngInject'
    return new TmrPagingService($state, $stateParams)
  }
}

/**
 * Paginated lists helper service, supporting both generic page-level
 * pagination and per-component namespaced pagination.
 *
 * @alias TmrPaging
 * @type {ngService}
 * @see tmrPagingRoutesConfig
 */
class TmrPagingService {
  constructor($state, $stateParams) {
    Object.assign(this, { $state, $stateParams })
  }

  /**
   * Extracts pagination parameters from a $transition$,
   * possibly mapping them from frontend keyed params
   * to backend unkeyed params (i.e.: `limit.someKey` to `limit`)
   * Must be called from resolve functions.
   *
   * @param {ngService|object} $transition$ The service from the routing system, or a simple paramters object
   * @param {object=} additionalParams
   * @param {boolean|string=} [paging=true] If true, adds per-route paging; if string, resolve one of the pagers of the route, defined in the route.params array
   */
  resolveParams($transition$ = {}, additionalParams = {}, paging = true) {
    let params =
      typeof $transition$.params === 'function'
        ? $transition$.params()
        : $transition$
    if (paging === true) {
      let { limit, offset, orderBy, asc } = params
      return Object.assign({ limit, offset, orderBy, asc }, additionalParams)
    }
    return Object.assign(
      {
        limit: params[`limit.${paging}`],
        offset: params[`offset.${paging}`],
        orderBy: params[`orderBy.${paging}`],
        asc: params[`asc.${paging}`],
      },
      additionalParams
    )
  }

  param(param, pagerId) {
    return typeof pagerId === 'string' ? `${param}.${pagerId}` : param
  }

  limit(pagerId) {
    return this.param('limit', pagerId)
  }

  offset(pagerId) {
    return this.param('offset', pagerId)
  }

  /**
   * Goes to another page on the current (paginated) state
   *
   * @param {number} page
   * @param {string=} pagerId An optional pager identifier for multiple pagers on the same route
   * @returns {Promise}
   */
  go(page, pagerId) {
    const offset = this.offset(pagerId)
    const limit = this.limit(pagerId)
    return this.$state.go('.', {
      [offset]: (page - 1) * this.$stateParams[limit],
    })
  }
}
